/**
 * /operations/payroll/payslip-distribution — Session 3B PR 13.
 * Owning agent: Khidmat (employee-facing delivery).
 */

import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, CheckCircle2, Mail, RefreshCw, Send } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { PayslipDistributionItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

function _statusToneClass(s: PayslipDistributionItem["status"]): string {
  if (s === "delivered") return "bg-emerald-100 text-emerald-800";
  if (s === "failed") return "bg-rose-100 text-rose-800";
  if (s === "sent") return "bg-brand-blue/15 text-brand-deep";
  return "bg-amber-100 text-amber-800";
}

export function PayslipDistributionPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const list = useQuery({
    queryKey: ["session3b-payslip-distribution", legalEntityId],
    queryFn: () => session3bApi.listPayslipDistribution(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: PayslipDistributionItem[] = list.data?.items ?? [];
  const periodLabel = items[0]?.period_label ?? "—";

  const counts = useMemo(() => {
    const failed = items.filter((r) => r.status === "failed").length;
    const pending = items.filter((r) => r.status === "generated").length;
    const delivered = items.filter((r) => r.status === "delivered" || r.status === "sent").length;
    return {
      total: items.length,
      attention: pending,
      critical: failed,
      delivered,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("payroll.payslip_distribution.title", "Payslip Distribution")}
          subtitle={t(
            "payroll.payslip_distribution.subtitle",
            "Generate + distribute payslips via WhatsApp (Khidmat). Track delivery status per employee per period.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="rounded-full border border-border-subtle bg-surface-sunken px-3 py-1 text-xs text-text-secondary">
            {t("payroll.payslip_distribution.period_label", "Current period")}: {periodLabel}
          </span>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "payroll.payslip_distribution.cta.generate_all_note",
                  "Generate All Payslips bulk-renders PDFs for every employee in the posted period.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <RefreshCw className="h-4 w-4" />
            {t("payroll.payslip_distribution.cta.generate_all", "Generate All Payslips")}
          </button>
        </div>

        {counts.critical > 0 && (
          <InlineAISuggestion
            agent="khidmat"
            icon={<AlertOctagon className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "payroll.payslip_distribution.suggestion.failed_headline",
              `${counts.critical} payslip${counts.critical === 1 ? "" : "s"} failed delivery — likely bad phone numbers on file.`,
            )}
            body={t(
              "payroll.payslip_distribution.suggestion.failed_body",
              "Khidmat can bulk-retry once the phone numbers are corrected. Open the master file for each failed employee to fix.",
            )}
            primaryAction={{
              label: t("payroll.payslip_distribution.suggestion.bulk_retry", "Bulk retry"),
              onClick: () =>
                showAlertTrace(
                  t(
                    "payroll.payslip_distribution.action.bulk_retry_note",
                    "Bulk-retry would re-queue every failed payslip for Khidmat WhatsApp delivery.",
                  ),
                ),
            }}
            suggestionKey="payslip-failed"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="khidmat"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.payslip_distribution.suggestion.empty_headline",
              "No payslips for the current open period yet.",
            )}
            body={t(
              "payroll.payslip_distribution.suggestion.empty_body",
              "Khidmat notes: payslips appear here as the period accumulates. Trigger Generate All Payslips once the period is posted.",
            )}
            primaryAction={{
              label: t("payroll.payslip_distribution.suggestion.open_period_lock", "Open Period Lock"),
              onClick: () => window.location.assign("/operations/payroll/period-lock"),
            }}
            suggestionKey="payslip-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("payroll.payslip_distribution.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("payroll.payslip_distribution.col.period", "Period")}</th>
                  <th className="px-3 py-2">{t("payroll.payslip_distribution.col.generated", "Generated")}</th>
                  <th className="px-3 py-2">{t("payroll.payslip_distribution.col.channel", "Channel")}</th>
                  <th className="px-3 py-2">{t("payroll.payslip_distribution.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("payroll.payslip_distribution.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.payslip_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.period_label}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.generated_at ?? "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "payroll.payslip_distribution.trace.channel",
                              `Channel = delivered_via from payslip_delivery_events. 'pending' = no delivery attempted yet. Khidmat picks WhatsApp first when phone_verified=true; falls back to email/portal.`,
                            ),
                          )
                        }
                      >
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {row.delivered_via}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "payroll.payslip_distribution.action.resend_note",
                                "Re-send payslip via Khidmat WhatsApp.",
                              ),
                            )
                          }
                          className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          <Send className="h-3 w-3" />
                          {t("payroll.payslip_distribution.action.resend", "Re-send")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="khidmat"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("payroll.payslip_distribution.title", "Payslip Distribution"),
            window_label: `${counts.delivered} delivered · ${counts.attention} pending`,
          }}
        />
      </aside>
    </div>
  );
}
